import '@inovua/reactdatagrid-community/index.css'
import React, { FC, useCallback, useMemo, useState } from 'react'
import { useTheme, Theme } from '@mui/material/styles'
import { useDispatch } from 'react-redux'
import ReactDataGrid from '@inovua/reactdatagrid-community'
import Box from '@mui/material/Box'
import { SxProps } from '@mui/system'
import Loading from '../system/Loading'

import {
  IField_Paginated_Sort_Direction,
} from '@kaidam/fc-shared/src/types/pagination'

import {
  PAGINATION_SORT_DIRECTION_DESCENDING,
  PAGINATION_SORT_DIRECTION_ASCENDING,
} from '@kaidam/fc-shared/src/constants'

import routerActions from '../../store/modules/router'

const gridStyle = {
  minHeight: 400,
  border: 'none',
  boxShadow: '0px 4px 10px 0px rgba(0,0,0,0.1)',
  width: '100%',
  height: '100%',
  position: 'relative',
  flexGrow: 1,
  flexBasis: '100%',
}

const getHeaderStyle = (theme: Theme) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  border: 'none',
  fontSize: '0.8rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  fontFamily: '"Open Sans", sans-serif, Arial, Helvetica',
  paddingLeft: '0.2rem',
  paddingRight: '0.2rem',
  height: '40px',
})

export interface IDataGrid2_Column_Render_Params<DataType = any> {
  value: any,
  data: DataType,
}

export interface IDataGrid2_Column<DataType = any> {
  name: string,
  header: string,
  defaultFlex?: number,
  defaultWidth?: number,
  minWidth?: number,
  sortable?: boolean,
  textAlign?: 'start' | 'center' | 'end',
  render?: (params: IDataGrid2_Column_Render_Params<DataType>) => any, 
}

type IGrid_Sort_Direction = -1 | 0 | 1

const SORT_DIRECTION_MAP: Record<IField_Paginated_Sort_Direction, IGrid_Sort_Direction> = {
  ASC: 1,
  DESC: -1,
}

interface DataGridProps<DataType = any> {
  idProperty?: string,
  rows: DataType[],
  columns: IDataGrid2_Column<DataType>[],
  sortColumn?: string,
  sortDirection?: IField_Paginated_Sort_Direction,
  sx?: SxProps,
  innerSx?: SxProps,
  loading?: boolean,
  userSelect?: boolean,
  minHeight?: number,
  rowHeight?: number,
  headerHeight?: number,
  autoSort?: boolean,
  editable?: boolean,
  onSort?: {
    (field: string, order: IField_Paginated_Sort_Direction): void,
  },
  onRowsChange?: {
    (rows: any[]): void,
  },
  onSelect?: {
    (rowIdx: number, colIdx: number): void,
  },
  onDoubleClick?: {
    (rowIdx: number): void,
  },
}

const DataGrid: FC<React.PropsWithChildren<DataGridProps>> = ({
  idProperty = 'id',
  rows,
  columns,
  sortColumn,
  sortDirection,
  sx = {},
  innerSx = {},
  userSelect = false,
  minHeight = 400,
  autoSort = false,
  editable = false,
  rowHeight = 56,
  headerHeight = 40,
  loading,
  onSort,
  onDoubleClick,
  onRowsChange,
  onSelect,
}) => {

  const [gridRef, setGridRef] = useState(null)
  const dispatch = useDispatch()
  const theme = useTheme()

  const onSortInfoChange = useCallback((value: any) => {
    let col: string = ''
    let order: IField_Paginated_Sort_Direction = PAGINATION_SORT_DIRECTION_ASCENDING
    if(!value) {
      col = sortColumn
      order = sortDirection == PAGINATION_SORT_DIRECTION_ASCENDING ? PAGINATION_SORT_DIRECTION_DESCENDING : PAGINATION_SORT_DIRECTION_ASCENDING
    }
    else {
      col = value.name
      order = value.dir > 0 ? PAGINATION_SORT_DIRECTION_ASCENDING : PAGINATION_SORT_DIRECTION_DESCENDING
    }

    if(!col) return

    if(autoSort) {
      dispatch(routerActions.mergeQueryParams({
        pagination_sort_column: col,
        pagination_sort_direction: order,
      }))
    }
    else if(onSort) {
      onSort(col, order)
    }
  }, [
    sortColumn,
    sortDirection,
    autoSort,
  ])

  const sortInfo = useMemo(() => {
    return {
      name: sortColumn,
      dir: SORT_DIRECTION_MAP[sortDirection],
    }
  }, [
    sortColumn,
    sortDirection,
  ])

  const useHeaderStyle = useMemo(() => {
    return getHeaderStyle(theme)
  }, [
    theme,
  ])

  const useColumns = useMemo(() => {
    const injectHeaderProps = (columns: IDataGrid2_Column<any>[]): IDataGrid2_Column<any>[] => {
      return columns.map((col, i) => {
        return Object.assign({}, col, {
          headerProps: {
            style: useHeaderStyle,
          },
        })
      })
    }
    if(!onRowsChange || !editable) return injectHeaderProps(columns)
    if(!gridRef || !gridRef.current) return injectHeaderProps(columns)
    const cellDOMProps = (cellProps: any) => {
      return {
        onClick: () => {
          gridRef.current.startEdit({ columnId: cellProps.id, rowIndex: cellProps.rowIndex })
        }
      }
    }
    const mappedColumns = columns.map(col => {
      return Object.assign({}, col, {
        editable: true,
        cellDOMProps,
      })
    })
    return injectHeaderProps(mappedColumns)
  }, [
    columns,
    editable,
    onRowsChange,
    gridRef,
    useHeaderStyle,
  ])

  const onEditComplete = useCallback(({
    value,
    columnId,
    rowId
  }: any) => {
    const newRows = rows.map((row, rowIndex) => {
      if(row[idProperty] != rowId) return row
      return Object.assign({}, row, {
        [columnId]: value,
      })
    })
    onRowsChange(newRows)
  }, [
    rows,
    idProperty,
    onRowsChange,
  ])

  const onCellClick = useCallback((ev: any, cellProps: any) => {
    if(!onSelect) return
    onSelect(cellProps.rowIndex, cellProps.columnIndex)
  }, [
    onSelect,
  ])

  return (
    <Box
      className='grid-wrapper'
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        position: 'relative',
        display: 'flex',
        overflow: 'auto',
        boxShadow: '0 2px 4px 0px rgba(0,0,0,0.2)',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        '& .InovuaReactDataGrid--theme-default-light .InovuaReactDataGrid__column-header__content': {
          fontWeight: 'lighter',
        },
        ...sx,
      }}
    >
      <Box
        className='Grid'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          flexGrow: 1,
          backgroundColor: '#fff',
          '& .rdg': {
            userSelect: ( userSelect ? 'auto' : 'none' )
          },
          minHeight: `${minHeight}px`,
          ...innerSx,
        }}
      >
        <ReactDataGrid
          columnUserSelect={ editable ? false : true }
          editable={ editable }
          onReady={ setGridRef }
          idProperty={ idProperty }
          columns={ useColumns }
          dataSource={ rows }
          sortInfo={ sortInfo }
          onSortInfoChange={ onSortInfoChange }
          onEditComplete={ onEditComplete }
          onCellClick={ onCellClick }
          onCellDoubleClick={ (ev, props) => onDoubleClick && onDoubleClick(props.rowIndex) }
          headerHeight={ headerHeight }
          minRowHeight={ rowHeight }
          rowHeight={ rowHeight }
          style={ gridStyle }
          showCellBorders={ true }
        />
      </Box>
      {
        loading && (
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              left: '0px',
              top: '0px',
              backgroundColor: 'rgba(255,255,255,0.8)',
            }}
          >
            <Loading />
          </Box>
        )
      }
    </Box>
    
  )
}

export default DataGrid